import React, { useMemo, useState } from 'react';
import { CheckCircleIcon, Loader2Icon, AlertCircleIcon, ClipboardCheckIcon } from 'lucide-react';
import { FieldCell, FieldConfig } from './FieldInput';
const DEMO_FIELDS: FieldConfig[] = [{
  id: 'fullName',
  label: 'Full Name',
  type: 'text'
}, {
  id: 'dob',
  label: 'Date of Birth',
  type: 'date'
}, {
  id: 'income',
  label: 'Annual Income',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'employmentStatus',
  label: 'Employment Status',
  type: 'select',
  options: ['Employed', 'Self-Employed', 'Retired', 'Unemployed']
}, {
  id: 'employerName',
  label: 'Employer Name',
  type: 'text'
}, {
  id: 'loanAmount',
  label: 'Loan Amount Requested',
  type: 'number',
  placeholder: '$0'
}, {
  id: 'propertyAddress',
  label: 'Property Address',
  type: 'text'
}, {
  id: 'loanType',
  label: 'Loan Type',
  type: 'select',
  options: ['Conventional', 'FHA', 'VA', 'USDA', 'Jumbo']
}];
export function RequiredFieldsCard() {
  const [savedValues, setSavedValues] = useState<Record<string, string>>({});
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  // Calculate progress
  const totalCount = DEMO_FIELDS.length;
  const completedCount = Object.keys(savedValues).filter(k => savedValues[k].trim() !== '').length;
  const remainingCount = totalCount - completedCount;
  const progressPercentage = completedCount / totalCount * 100;
  // Filter to only show incomplete fields (not yet saved with a value)
  const visibleFields = useMemo(() => {
    return DEMO_FIELDS.filter(field => {
      const saved = savedValues[field.id] || '';
      return saved.trim() === '';
    });
  }, [savedValues]);
  // Check if there are unsaved changes
  const isDirty = useMemo(() => {
    return Object.entries(pendingEdits).some(([key, val]) => {
      const savedVal = savedValues[key] || '';
      return val.trim() !== savedVal.trim();
    });
  }, [pendingEdits, savedValues]);
  const handleFieldChange = (id: string, value: string) => {
    setPendingEdits(prev => ({
      ...prev,
      [id]: value
    }));
  };
  const handleSave = () => {
    setIsSaving(true);
    // Simulate network request
    setTimeout(() => {
      setSavedValues(prev => {
        const newSaved = {
          ...prev
        };
        Object.entries(pendingEdits).forEach(([key, val]) => {
          newSaved[key] = val;
        });
        return newSaved;
      });
      setPendingEdits({});
      setIsSaving(false);
    }, 600);
  };
  const handleCancel = () => {
    setPendingEdits({});
  };
  return <div className="bg-white rounded-2xl shadow-lg shadow-slate-200/50 border border-slate-200 overflow-hidden max-w-4xl mx-auto flex flex-col relative" data-id="element-22">
      {/* Top Accent Border */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-600 z-10" data-id="element-23" />

      {/* Save/Cancel Toolbar */}
      {isDirty && <div className="px-6 py-3 bg-indigo-50/80 backdrop-blur-sm border-b border-indigo-100 flex items-center justify-between animate-in slide-in-from-top-4 fade-in duration-300 z-0 mt-1" data-id="element-24">
          <div className="flex items-center gap-2.5 text-sm text-indigo-900 font-medium" data-id="element-25">
            <AlertCircleIcon className="w-4.5 h-4.5 text-indigo-600" data-id="element-26" />
            <span data-id="element-27">You have unsaved changes</span>
          </div>
          <div className="flex items-center gap-3" data-id="element-28">
            <button onClick={handleCancel} disabled={isSaving} className="px-4 py-2 text-sm font-medium text-indigo-700 hover:bg-indigo-100/80 rounded-lg transition-colors disabled:opacity-50" data-id="element-29">
              Cancel
            </button>
            <button onClick={handleSave} disabled={isSaving} className="inline-flex items-center justify-center px-5 py-2 text-sm font-semibold text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 shadow-sm shadow-indigo-200 transition-all disabled:opacity-70 min-w-[80px]" data-id="element-30">
              {isSaving ? <Loader2Icon className="w-4 h-4 animate-spin" data-id="element-31" /> : 'Save Changes'}
            </button>
          </div>
        </div>}

      {/* Premium Header */}
      <div className={`px-6 py-5 border-b border-slate-200 bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${!isDirty ? 'mt-1' : ''}`} data-id="element-32">
        <div data-id="element-33">
          <h2 className="text-lg font-semibold text-slate-900 tracking-tight" data-id="element-34">
            Required Information
          </h2>
          <p className="text-sm text-slate-500 mt-0.5" data-id="element-35">
            {remainingCount === 0 ? 'All fields complete' : `Please complete the remaining ${remainingCount} field${remainingCount !== 1 ? 's' : ''}`}
          </p>
        </div>

        <div className="flex flex-col items-end gap-2" data-id="element-36">
          <span className="text-sm font-medium text-slate-700" data-id="element-37">
            {completedCount} / {totalCount} complete
          </span>
          {/* Segmented Progress Bar */}
          <div className="flex gap-1 w-full sm:w-48 h-2" data-id="element-38">
            {DEMO_FIELDS.map((field, idx) => {
            const isFilled = (savedValues[field.id] || '').trim() !== '';
            return <div key={idx} className={`flex-1 rounded-full transition-colors duration-500 ${isFilled ? 'bg-indigo-600' : 'bg-slate-100'}`} data-id="element-39" />;
          })}
          </div>
        </div>
      </div>

      {/* Completion Banner */}
      {remainingCount === 0 && !isDirty && <div className="px-6 py-5 bg-gradient-to-r from-emerald-50 to-teal-50 border-b border-emerald-100 flex items-start sm:items-center gap-4" data-id="element-40">
          <div className="bg-emerald-100 p-2 rounded-full flex-shrink-0" data-id="element-41">
            <CheckCircleIcon className="w-6 h-6 text-emerald-600" data-id="element-42" />
          </div>
          <div data-id="element-43">
            <h3 className="text-base font-semibold text-emerald-900" data-id="element-44">
              Application Ready
            </h3>
            <p className="text-sm text-emerald-700 mt-0.5" data-id="element-45">
              All required fields are complete. Your application is ready for
              review by your loan officer.
            </p>
          </div>
        </div>}

      {/* Two-Column Grid Data Table or Empty State */}
      {visibleFields.length > 0 ? <div className="grid grid-cols-1 md:grid-cols-2 bg-white" data-id="element-46">
          {visibleFields.map((field, index) => {
        const currentValue = pendingEdits[field.id] ?? savedValues[field.id] ?? '';
        const savedValue = savedValues[field.id] ?? '';
        // Calculate grid borders
        const isLastInRow = index % 2 !== 0;
        const isLastRow = index >= visibleFields.length - (visibleFields.length % 2 === 0 ? 2 : 1);
        return <FieldCell key={field.id} field={field} value={currentValue} savedValue={savedValue} onChange={val => handleFieldChange(field.id, val)} isLastInRow={isLastInRow} isLastRow={isLastRow} data-id="element-47" />;
      })}
        </div> : <div className="px-6 py-16 flex flex-col items-center justify-center text-center" data-id="element-48">
          <div className="bg-slate-100 p-3.5 rounded-full mb-4" data-id="element-49">
            <ClipboardCheckIcon className="w-7 h-7 text-slate-400" data-id="element-50" />
          </div>
          <p className="text-base font-semibold text-slate-700" data-id="element-51">
            No Required Fields Missing
          </p>
          <p className="text-sm text-slate-400 mt-1" data-id="element-52">
            All required information has been provided.
          </p>
        </div>}
    </div>;
}